import { useState, useRef, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Contacto() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const [cotizStatus, setCotizStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [cotizCharCount, setCotizCharCount] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const addRef = (el: HTMLDivElement | null, index: number) => {
    sectionRefs.current[index] = el;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msgField = form.querySelector("textarea[name='mensaje']") as HTMLTextAreaElement;
    if (msgField && msgField.value.length > 500) {
      return;
    }
    setStatus("sending");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d7a2qs32hsar0k5m1040", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[45vh] min-h-[320px] flex items-center overflow-hidden">
        {/* Fondo sólido oscuro */}
        <div className="absolute inset-0 bg-[#0d1b26]" />
        {/* Patrón de puntos decorativo */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, #f97316 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
        {/* Blob naranja izquierda */}
        <div className="absolute -left-24 top-1/2 -translate-y-1/2 w-[480px] h-[480px] rounded-full bg-orange-500/20 blur-3xl" />
        {/* Blob naranja derecha */}
        <div className="absolute right-0 bottom-0 w-[320px] h-[320px] rounded-full bg-orange-400/10 blur-2xl" />
        {/* Línea inferior naranja */}
        <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-orange-500 via-orange-400 to-transparent" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Estamos aquí para ayudarte
            </span>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight">
              <span className="text-orange-400">Contacto</span>
            </h1>
            <p className="mt-4 text-gray-300 text-lg max-w-xl">
              Escríbenos o llámanos. Te responderemos a la brevedad.
            </p>
          </div>
          {/* Tarjetas info rápida */}
          <div className="hidden lg:flex flex-col gap-3 shrink-0">
            {[
              { icon: "ri-phone-line", label: "Llámanos", value: "51 2 247995" },
              { icon: "ri-mail-line", label: "Escríbenos", value: "chj@charlesjuarez.cl" },
              { icon: "ri-map-pin-2-line", label: "Ubícanos", value: "Coquimbo, Chile" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500/20">
                  <i className={`${item.icon} text-orange-400 text-base`} />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">{item.label}</p>
                  <p className="text-white text-sm font-semibold">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Info */}
          <div
            ref={(el) => addRef(el, 0)}
            className="opacity-0 translate-y-8 transition-all duration-700"
          >
            <span className="inline-block bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Información de Contacto
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1c2b38] mb-8">
              Hablemos de tu proyecto
            </h2>

            <div className="flex flex-col gap-6 mb-10">
              {[
                {
                  icon: "ri-map-pin-2-line",
                  label: "Dirección",
                  value: "Av. Molino 1260, Barrio Industrial, Coquimbo",
                },
                {
                  icon: "ri-mail-send-line",
                  label: "Correo electrónico",
                  value: "chj@charlesjuarez.cl",
                  href: "mailto:chj@charlesjuarez.cl",
                },
                {
                  icon: "ri-phone-line",
                  label: "Teléfono / Fax",
                  value: "51 2 247995",
                  href: "tel:512247995",
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-orange-50 shrink-0">
                    <i className={`${item.icon} text-lg text-orange-500`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-[#1c2b38] font-semibold hover:text-orange-500 transition-colors cursor-pointer">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-[#1c2b38] font-semibold">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Mapa */}
            <div className="rounded-2xl overflow-hidden h-64 border border-gray-100">
              <iframe
                title="Ubicación Charles Juárez"
                src="https://maps.google.com/maps?q=Av.+Molino+1260,+Barrio+Industrial,+Coquimbo,+Chile&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Formulario */}
          <div
            ref={(el) => addRef(el, 1)}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-150"
          >
            <div className="bg-[#f7f8fa] rounded-2xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-[#1c2b38] mb-6">Envíanos un mensaje</h3>

              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100">
                    <i className="ri-check-line text-3xl text-green-500" />
                  </div>
                  <h4 className="text-[#1c2b38] font-bold text-lg">¡Mensaje enviado!</h4>
                  <p className="text-gray-500 text-sm">Te responderemos a la brevedad. Gracias por contactarnos.</p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="whitespace-nowrap mt-2 px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full transition-colors cursor-pointer"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contacto-juarez"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        required
                        placeholder="Tu nombre"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                        Empresa
                      </label>
                      <input
                        type="text"
                        name="empresa"
                        placeholder="Tu empresa"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                      Correo electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="tu@correo.cl"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      placeholder="+56 9 XXXX XXXX"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                      Servicio de interés
                    </label>
                    <select
                      name="servicio"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer"
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="Arriendo de Maquinarias">Arriendo de Maquinarias</option>
                      <option value="Grúas Plumas">Grúas Plumas</option>
                      <option value="Obras Civiles">Obras Civiles</option>
                      <option value="Maestranza">Maestranza</option>
                      <option value="Transporte de Carga">Transporte de Carga</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                      Mensaje *
                    </label>
                    <textarea
                      name="mensaje"
                      required
                      rows={4}
                      maxLength={500}
                      placeholder="Cuéntanos sobre tu proyecto..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? "text-orange-500" : "text-gray-400"}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {status === "error" && (
                    <p className="text-red-500 text-sm text-center">
                      Hubo un error al enviar. Por favor intenta nuevamente.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending" || charCount > 500}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 cursor-pointer"
                  >
                    {status === "sending" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar mensaje
                        <i className="ri-send-plane-line" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de Cotización */}
      <section className="py-20 bg-[#f7f8fa] border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Solicitud de Presupuesto
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1c2b38] mb-4">
              Solicita tu Cotización
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Completa el formulario con los detalles de tu proyecto y te enviaremos una cotización personalizada a la brevedad.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10">
            {cotizStatus === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100">
                  <i className="ri-check-double-line text-3xl text-green-500" />
                </div>
                <h4 className="text-[#1c2b38] font-bold text-xl">¡Cotización enviada!</h4>
                <p className="text-gray-500 text-sm max-w-sm">
                  Recibimos tu solicitud. Nuestro equipo la revisará y te contactará con una cotización personalizada.
                </p>
                <button
                  onClick={() => setCotizStatus("idle")}
                  className="whitespace-nowrap mt-2 px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full transition-colors cursor-pointer"
                >
                  Nueva cotización
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="cotizacion-juarez"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const desc = form.querySelector("textarea[name='descripcion']") as HTMLTextAreaElement;
                  if (desc && desc.value.length > 500) return;
                  setCotizStatus("sending");
                  const data = new URLSearchParams();
                  const formData = new FormData(form);
                  formData.forEach((value, key) => { data.append(key, value.toString()); });
                  try {
                    const res = await fetch("https://readdy.ai/api/form/d7a3akj86jhav3jpfua0", {
                      method: "POST",
                      headers: { "Content-Type": "application/x-www-form-urlencoded" },
                      body: data.toString(),
                    });
                    if (res.ok) { setCotizStatus("success"); form.reset(); setCotizCharCount(0); }
                    else setCotizStatus("error");
                  } catch { setCotizStatus("error"); }
                }}
                className="flex flex-col gap-6"
              >
                {/* Fila 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Nombre completo *</label>
                    <input type="text" name="nombre" required placeholder="Tu nombre" className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Empresa / Razón Social</label>
                    <input type="text" name="empresa" placeholder="Nombre de tu empresa" className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors" />
                  </div>
                </div>

                {/* Fila 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Correo electrónico *</label>
                    <input type="email" name="email" required placeholder="tu@correo.cl" className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Teléfono *</label>
                    <input type="tel" name="telefono" required placeholder="+56 9 XXXX XXXX" className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors" />
                  </div>
                </div>

                {/* Fila 3 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Servicio requerido *</label>
                    <select name="servicio" required className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer">
                      <option value="">Selecciona un servicio</option>
                      <option value="Arriendo de Maquinarias">Arriendo de Maquinarias</option>
                      <option value="Grúa Pluma">Grúa Pluma</option>
                      <option value="Transporte Cama Baja">Transporte Cama Baja</option>
                      <option value="Camión Pluma">Camión Pluma</option>
                      <option value="Maestranza Industrial">Maestranza Industrial</option>
                      <option value="Obras Civiles">Obras Civiles</option>
                      <option value="Múltiples Servicios">Múltiples Servicios</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Duración estimada</label>
                    <select name="duracion" className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer">
                      <option value="">Selecciona duración</option>
                      <option value="1 día">1 día</option>
                      <option value="2 a 5 días">2 a 5 días</option>
                      <option value="1 semana">1 semana</option>
                      <option value="2 a 4 semanas">2 a 4 semanas</option>
                      <option value="1 a 3 meses">1 a 3 meses</option>
                      <option value="Más de 3 meses">Más de 3 meses</option>
                    </select>
                  </div>
                </div>

                {/* Fila 4 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Fecha de inicio requerida</label>
                    <input type="date" name="fecha_inicio" className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors cursor-pointer" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Lugar de trabajo</label>
                    <input type="text" name="lugar" placeholder="Ciudad o dirección" className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors" />
                  </div>
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">Descripción del trabajo *</label>
                  <textarea
                    name="descripcion"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Describe con detalle el trabajo que necesitas: tipo de carga, dimensiones, condiciones del terreno, accesos, etc."
                    onChange={(e) => setCotizCharCount(e.target.value.length)}
                    className="w-full bg-[#f7f8fa] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 transition-colors resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${cotizCharCount > 480 ? "text-orange-500" : "text-gray-400"}`}>
                    {cotizCharCount}/500
                  </p>
                </div>

                {cotizStatus === "error" && (
                  <p className="text-red-500 text-sm text-center">Hubo un error al enviar. Por favor intenta nuevamente.</p>
                )}

                <button
                  type="submit"
                  disabled={cotizStatus === "sending" || cotizCharCount > 500}
                  className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-all duration-200 cursor-pointer text-base"
                >
                  {cotizStatus === "sending" ? (
                    <><i className="ri-loader-4-line animate-spin" /> Enviando cotización...</>
                  ) : (
                    <><i className="ri-file-list-3-line" /> Solicitar Cotización</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
